/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useCallback, useEffect, useState } from '@web-stories-wp/react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Modal } from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { useAPI } from '../../../../app/api';
import TemplateFolderSelect from './templateFolderSelect';

const TITLE_FIELD_ID = 'save-template-title';
const FOLDER_FIELD_ID = 'save-template-folder';

const DialogBox = styled.form`
  position: relative;
  width: min(440px, calc(100vw - 32px));
  height: ${({ $hasError }) => ($hasError ? 286 : 267)}px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: #000;
  border: 0;
  border-radius: 8px;
  box-sizing: border-box;
  box-shadow: inset 0 0 0 1px #3a3d3f;
  color: #fff;
  overflow: visible;
  font-family: ${({ theme }) => theme.typography.family.primary};
`;

const FieldError = styled.div`
  margin-top: -12px;
  color: #ff8b8b;
  font-size: 12px;
  line-height: 16px;
`;

const DialogTitle = styled.h2`
  margin: 0;
  height: 19px;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  line-height: 19px;
  letter-spacing: 0;
`;

const FieldGroup = styled.div`
  position: relative;
  height: 58px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  height: 16px;
  color: #fff;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0;
`;

const ControlWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 36px;
`;

const TextInput = styled.input`
  width: 100%;
  height: 36px;
  padding: 0 12px;
  box-sizing: border-box;
  background: #000;
  border: 1px solid #393d3f;
  border-radius: 4px;
  color: #fff;
  font-family: ${({ theme }) => theme.typography.family.primary};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  outline: none;

  &::placeholder {
    color: #919899;
    opacity: 1;
  }

  &:focus {
    border-color: #393d3f;
    outline: 2px solid #76a0f0;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Footer = styled.div`
  height: 32px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ActionButton = styled.button`
  width: ${({ $width }) => $width}px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: ${({ $primary }) => ($primary ? '#1c62eb' : '#3c434a')};
  color: #fff;
  cursor: pointer;
  font-family: ${({ theme }) => theme.typography.family.primary};
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0;
  opacity: ${({ disabled, $primary }) => (disabled && $primary ? 0.5 : 1)};

  &:disabled {
    cursor: not-allowed;
  }
`;

function SaveTemplateDialog({
  initialFolderId = '',
  initialTitle = '',
  isOpen,
  isSaving,
  onClose,
  onSave,
}) {
  const {
    actions: { getStudioTemplateFolderDestinations },
  } = useAPI();

  const [title, setTitle] = useState(initialTitle);
  const [folderInputValue, setFolderInputValue] = useState(initialFolderId);
  const [folders, setFolders] = useState([]);
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);
  const [folderLoadError, setFolderLoadError] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setTitle(initialTitle);
    setFolderInputValue(initialFolderId);
    setIsLoadingFolders(true);
    setFolderLoadError(false);
    let isCurrent = true;
    const controller = new AbortController();

    const loadDestinations = async () => {
      try {
        const { folders: allFolders } =
          await getStudioTemplateFolderDestinations(controller.signal);

        if (isCurrent) {
          setFolders(allFolders);
        }
      } catch (error) {
        if (isCurrent) {
          setFolders([]);
          setFolderLoadError(true);
        }
      } finally {
        if (isCurrent) {
          setIsLoadingFolders(false);
        }
      }
    };

    loadDestinations();
    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [
    initialFolderId,
    initialTitle,
    isOpen,
    getStudioTemplateFolderDestinations,
  ]);

  const canSave =
    title.trim().length > 0 &&
    folders.some((folder) => folder.id === folderInputValue) &&
    !isLoadingFolders &&
    !folderLoadError &&
    !isSaving;

  const handleSave = useCallback(() => {
    if (!canSave) {
      return;
    }

    onSave({ title: title.trim(), folderId: folderInputValue });
  }, [canSave, folderInputValue, onSave, title]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaHideApp={false}
      contentLabel="Save as template"
      contentStyles={{
        overflow: 'visible',
      }}
    >
      <DialogBox
        $hasError={folderLoadError}
        onSubmit={(event) => {
          event.preventDefault();
          handleSave();
        }}
      >
        <DialogTitle>Save as template</DialogTitle>
        <FieldGroup>
          <FieldLabel htmlFor={TITLE_FIELD_ID}>Template title</FieldLabel>
          <ControlWrapper>
            <TextInput
              id={TITLE_FIELD_ID}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Template Title..."
              disabled={isSaving}
              autoComplete="off"
              autoFocus
            />
          </ControlWrapper>
        </FieldGroup>
        <FieldGroup>
          <FieldLabel htmlFor={FOLDER_FIELD_ID}>Folder</FieldLabel>
          <ControlWrapper>
            <TemplateFolderSelect
              inputId={FOLDER_FIELD_ID}
              value={folderInputValue}
              onChange={setFolderInputValue}
              folders={folders}
              isLoading={isLoadingFolders}
              placeholder={
                isLoadingFolders
                  ? 'Loading folders...'
                  : 'Choose an existing folder...'
              }
              disabled={isSaving || isLoadingFolders}
            />
          </ControlWrapper>
        </FieldGroup>
        {folderLoadError && (
          <FieldError role="alert">
            Unable to load folders. Close this dialog and try again.
          </FieldError>
        )}
        <Footer>
          <ActionButton type="button" $width={83} onClick={onClose}>
            Cancel
          </ActionButton>
          <ActionButton type="submit" $width={70} $primary disabled={!canSave}>
            Save
          </ActionButton>
        </Footer>
      </DialogBox>
    </Modal>
  );
}

SaveTemplateDialog.propTypes = {
  initialFolderId: PropTypes.string,
  initialTitle: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default SaveTemplateDialog;
